import React from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Anchor,
  Button,
  Divider,
  Image,
  Loader,
  NumberInput
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { GripVertical, Plus, Trash } from 'tabler-icons-react';
import AppFlexbox from '../../common/AppFlexbox';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import SortableList from '../../common/SortableList';
import { getEcomVendorProductStatus } from '../../../helpers/vendorHelper';
import AppCard from '../../common/AppCard';
import { useModalState } from '../../../helpers/hooks';
import EditProductCard from '../products/EditProductCard';
import ActionableIcon from '../../common/ActionableIcon';
import VendorProductAssignModal from './VendorProductAssignModal';

const VendorCollectionProductSelectFormSection = ({
  title,
  subTitle,
  selectedProducts,
  onChange,
  isSubmitting,
  enableQuantityChange,
  showVendorName,
  loading
}) => {
  const navigate = useNavigate();
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();

  return (
    <EditProductCard
      contentProps={{ style: { padding: 0 } }}
      title={title}
      titleAction={
        <AppFlexbox style={{ gap: 8 }}>
          <Button
            color="dark"
            disabled={isSubmitting}
            leftSection={<Plus />}
            onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.CREATE)}
            size="compact-sm"
            type="button"
            variant="filled"
          >
            Add
          </Button>
        </AppFlexbox>
      }
    >
      <AppStack style={{ gap: 0 }}>
        {subTitle && (
          <AppStack style={{ padding: 16, paddingTop: 0 }}>
            {typeof subTitle === 'string' ? (
              <AppText style={{ fontSize: 14, color: '#666' }}>
                {subTitle}
              </AppText>
            ) : (
              subTitle
            )}
          </AppStack>
        )}

        {loading ? (
          <>
            <Divider />
            <AppStack
              style={{
                gap: 5,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 10px',
                flex: 1
              }}
            >
              <Loader color="dark" size={34} />
            </AppStack>
          </>
        ) : selectedProducts.length === 0 ? (
          <>
            <Divider />
            <AppStack
              style={{
                padding: 16,
                gap: 10,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AppText
                style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
              >
                No products added.
              </AppText>
              <Button
                color="dark"
                disabled={isSubmitting}
                leftSection={<Plus />}
                onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.CREATE)}
                size="compact-sm"
                type="button"
                variant="outline"
              >
                Add product
              </Button>
            </AppStack>
          </>
        ) : (
          <SortableList
            items={selectedProducts}
            onChange={(newList) => {
              onChange(
                newList.map((p, index) => ({
                  ...p,
                  sort: index + 1
                }))
              );
            }}
            renderItem={(
              item,
              {
                attributes,
                listeners,
                isDragging,
                setNodeRef,
                style,
                isOverlay
              }
            ) => {
              const productStatusInfo = item
                ? getEcomVendorProductStatus(item)
                : {};

              return (
                <AppCard
                  ref={setNodeRef}
                  radius={0}
                  shadow="none"
                  style={{
                    ...style,
                    padding: 0,
                    outline: isOverlay ? 'solid 1px #dee2e6' : 'none',
                    backgroundColor: isDragging
                      ? 'rgba(56, 56, 56, 0.1)'
                      : '#FFF',
                    opacity: isDragging ? 0.4 : 1,
                    cursor: isDragging ? 'grabbing' : 'default'
                  }}
                >
                  <AppFlexbox
                    style={{
                      padding: 10,
                      gap: 10,
                      borderTop: isOverlay ? 'none' : 'solid 1px #dee2e6',
                      flex: 1,
                      alignItems: 'center'
                    }}
                  >
                    <ActionIcon
                      color="dark"
                      disabled={isSubmitting}
                      variant="subtle"
                      {...attributes}
                      {...listeners}
                    >
                      <GripVertical size={20} />
                    </ActionIcon>

                    <AppFlexbox
                      style={{
                        flex: 1,
                        gap: 10,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <AppFlexbox style={{ alignItems: 'center' }}>
                        <AppStack
                          style={{
                            width: 40,
                            height: 40,
                            border: !item.previewImage
                              ? 'solid 1px #dee2e6'
                              : 'none'
                          }}
                        >
                          <Image h={40} src={item.previewImage} w={40} />
                        </AppStack>
                        <AppStack style={{ gap: 0 }}>
                          <Anchor
                            onClick={() =>
                              navigate(`/vendor/products/${item.uuid}`)
                            }
                            style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: '#000'
                            }}
                          >
                            {item.name}
                          </Anchor>
                          {showVendorName && (
                            <AppText
                              style={{
                                fontSize: 14,
                                color: '#666'
                              }}
                            >
                              {item.vendorName}
                            </AppText>
                          )}

                          <AppText
                            style={{
                              fontSize: 12,
                              color: productStatusInfo.color
                            }}
                          >
                            {productStatusInfo.label}
                          </AppText>
                        </AppStack>
                      </AppFlexbox>

                      <AppFlexbox style={{ alignItems: 'center', gap: 8 }}>
                        {enableQuantityChange && (
                          <NumberInput
                            disabled={isSubmitting || loading}
                            min={1}
                            onChange={(v) => {
                              onChange(
                                [...selectedProducts].map((p) =>
                                  p.key === item.key
                                    ? { ...p, count: Math.max(v, 1) }
                                    : p
                                )
                              );
                            }}
                            placeholder="Quantity"
                            radius={0}
                            size="sm"
                            style={{ maxWidth: 150 }}
                            styles={{
                              label: {
                                fontSize: 14,
                                fontWeight: 500
                              }
                            }}
                            value={item.count}
                          />
                        )}

                        <ActionableIcon
                          color="red"
                          disabled={isSubmitting}
                          onClick={() =>
                            onChange(
                              selectedProducts.filter(
                                (p) => p.uuid !== item.uuid
                              )
                            )
                          }
                          radius="md"
                          variant="subtle"
                        >
                          <Trash />
                        </ActionableIcon>
                      </AppFlexbox>
                    </AppFlexbox>
                  </AppFlexbox>
                </AppCard>
              );
            }}
          />
        )}

        <VendorProductAssignModal
          isOpen={
            modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.CREATE
          }
          onClose={onCloseModal}
          onConfirm={(products) => {
            const newEcomVendorProducts = products.filter(
              (p) => !selectedProducts.find((f) => f.uuid === p.uuid)
            );
            onChange(
              [
                ...selectedProducts.filter((p) =>
                  products.some((prod) => prod.uuid === p.uuid)
                ),
                ...newEcomVendorProducts.map((p, i) => ({
                  ...p,
                  sort: selectedProducts.length + i + 1,
                  count: 1,
                  key: new Date().getTime() + i
                }))
              ]
                .sort((a, b) => a.sort - b.sort)
                .map((p, i) => ({ ...p, sort: i + 1 }))
            );

            onCloseModal();
          }}
          selectedProducts={selectedProducts}
          showCategoryFilter
          showCollectionFilter
          showDesignFilter
        />
      </AppStack>
    </EditProductCard>
  );
};

VendorCollectionProductSelectFormSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  enableQuantityChange: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  selectedProducts: PropTypes.array,
  showVendorName: PropTypes.bool,
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.string
};

export default VendorCollectionProductSelectFormSection;
